/**
 * Keyboard Configuration Types
 * Based on spec §5 - Format konfiguracji (NORMATIVE)
 */

import type { HexCoord } from './hex';
import type { KeyAction } from './midi';

// Spec §5.1: Config version (NORMATIVE)
export type ConfigVersion = '2.0.0';

// Spec §5.2: Module grid
export interface ModuleGrid {
  columns: number; // 1-8
  rows: number; // 1-4
  keys_per_module?: 12 | 16; // 12-key (3x4) or 16-key (4x4), default 16
}

// Spec §5.3: Note mapping (NORMATIVE)
export interface NoteMapping {
  origin: HexCoord;
  base_note: number; // 0-127
  intervals: {
    q: number; // semitones per q-step (-127 to 127)
    r: number; // semitones per r-step (-127 to 127)
  };
  // v2.0 fields (present when tuning enabled)
  cents_mode?: boolean;           // true = use cent fields instead of intervals
  interval_q_cents?: number;      // 0.1-cent per q-step (int16 range: -32768..32767)
  interval_r_cents?: number;      // 0.1-cent per r-step (int16 range: -32768..32767)
  pitch_bend_range?: number;      // semitones, default 48
}

// Spec §5.4: MIDI settings
export interface MidiSettings {
  default_channel: number; // 0-15
}

// Spec §5.5: Layer definition
export interface Layer {
  id: number; // 0-15
  name: string;
  default?: boolean; // Only one layer should have default: true
  note_mapping?: NoteMapping; // Optional per-layer note mapping (falls back to global if not set)
}

// Spec §5.6: Key override (per-key MIDI action)
export interface KeyOverride {
  pos: HexCoord; // Global coordinates
  layer: number; // 0-15
  action: KeyAction;
}

// Spec §5.10: Global LED settings (USER-EDITABLE)
export interface LEDSettings {
  enabled: boolean;
  brightness: number; // 0.0-1.0
  perceptual_profile?: 'default' | 'soft' | 'vivid';
  update_rate_hz: number; // 30-200
  fade_default_ms: number; // 0-500
}

// Spec §5.11: Default LED behavior
export interface LEDState {
  color: [number, number, number]; // RGB [0-255, 0-255, 0-255]
  brightness: number; // 0.0-1.0
  fade_ms?: number; // Optional fade time
}

export interface LEDDefaultBehavior {
  idle: LEDState;
  pressed: LEDState;
}

// Spec §5.12: LED override per key
export interface LEDOverride {
  pos: HexCoord; // Global coordinates
  layer: number; // 0-15
  idle: LEDState;
  pressed?: LEDState; // Optional, defaults to idle if not specified
}

// Spec §5.6: Zone bounds
export interface ZoneBounds {
  q_min: number;
  q_max: number;
  r_min?: number;  // Optional - if omitted, applies to all r values
  r_max?: number;  // Optional - if omitted, applies to all r values
}

// Spec §5.6: Zone definition
export interface Zone {
  name: string;        // max 32 chars
  bounds: ZoneBounds;
  channel: number;     // 0-15
  transpose: number;   // -24 to +24 semitones
  transpose_cents?: number;       // 0.1-cent (v2.0)
  note_mapping?: NoteMapping;     // optional per-zone override (v2.0)
}

// Spec §5.13: LED per layer
export type LEDLayerMode = 'off' | 'dim' | 'full' | 'pulse';

export interface LEDLayerConfig {
  layer: number; // 0-15
  base_color: [number, number, number]; // RGB
  brightness: number; // 0.0-1.0
  mode: LEDLayerMode;
}

// Spec §5.14: Scale type definitions
export type ScaleType =
  | 'major'
  | 'natural_minor'
  | 'harmonic_minor'
  | 'melodic_minor'
  | 'pentatonic_major'
  | 'pentatonic_minor'
  | 'blues'
  | 'dorian'
  | 'phrygian'
  | 'lydian'
  | 'mixolydian'
  | 'whole_tone'
  | 'chromatic';

// Scale bitmasks: 1 = note in scale, indexed by semitone offset from root
export const SCALE_MASKS: Record<ScaleType, readonly number[]> = {
  major:            [1,0,1,0,1,1,0,1,0,1,0,1],
  natural_minor:    [1,0,1,1,0,1,0,1,1,0,1,0],
  harmonic_minor:   [1,0,1,1,0,1,0,1,1,0,0,1],
  melodic_minor:    [1,0,1,1,0,1,0,1,0,1,0,1],
  pentatonic_major: [1,0,1,0,1,0,0,1,0,1,0,0],
  pentatonic_minor: [1,0,0,1,0,1,0,1,0,0,1,0],
  blues:            [1,0,0,1,0,1,1,1,0,0,1,0],
  dorian:           [1,0,1,1,0,1,0,1,0,1,1,0],
  phrygian:         [1,1,0,1,0,1,0,1,1,0,1,0],
  lydian:           [1,0,1,0,1,0,1,1,0,1,0,1],
  mixolydian:       [1,0,1,0,1,1,0,1,0,1,1,0],
  whole_tone:       [1,0,1,0,1,0,1,0,1,0,1,0],
  chromatic:        [1,1,1,1,1,1,1,1,1,1,1,1],
};

// Spec §5.14: Scale coloring
export interface ScaleColoringConfig {
  enabled: boolean;
  root_note: number; // 0-127
  root_degree?: number;           // 0 to N-1 (v2.0, degree in tuning table)
  scale_type: ScaleType;
  colors: {
    root: [number, number, number];
    in_scale: [number, number, number];
    non_scale: [number, number, number];
  };
  color_mode?: 'three_color' | 'per_degree';  // default 'three_color'
  degree_colors?: [number, number, number][];  // length = tuningTable.notes_per_period
}

/**
 * v2.0: Tuning table for microtonal scales.
 * All pitch values in 0.1-cent units (1 semitone = 1000).
 */
export interface TuningTable {
  notes_per_period: number;    // 1-128 (e.g. 19 for 19-EDO)
  period_is_octave: boolean;   // true if period = octave
  period_cents: number;        // 0.1-cent units (12000 = 1200.0 cents = octave)
  pitches: number[];           // int32[], 0.1-cent, ascending within period, length = notes_per_period
  scale_mask: boolean[];       // length = notes_per_period, true = in-scale
  pitch_labels?: string[];     // original ratio/cents text from .scl (e.g. "9/8", "386.3c"), length = notes_per_period
}

// Spec §5.18: Analog input MIDI types (v1.4)
export type SliderMidiType = 'disabled' | 'cc' | 'pitch_bend' | 'aftertouch';

// Spec §5.18: Pedal detection modes (v1.4)
export type PedalDetectMode = 'auto' | 'sustain' | 'expression';

// Spec §5.18: Slider configuration (v1.4)
export interface SliderConfig {
  midi_type: SliderMidiType;
  channel: number;  // 0-15
  cc: number;       // 0-127, used when midi_type is 'cc'
  min: number;      // 0-127, output minimum
  max: number;      // 0-127, output maximum
  invert: boolean;  // true to invert the slider direction
  spring_center: boolean;  // true for spring-return wheel (center = rest position)
}

// Spec §5.18: Pedal sustain configuration (v1.4)
export interface PedalSustainConfig {
  cc: number;       // 0-127, default 64
  channel: number;  // 0-15
  invert: boolean;  // true to invert sustain on/off
}

// Spec §5.18: Pedal expression configuration (v1.4)
export interface PedalExpressionConfig {
  cc: number;       // 0-127, default 11
  channel: number;  // 0-15
  min: number;      // 0-127, output minimum
  max: number;      // 0-127, output maximum
  invert: boolean;  // true to invert expression direction
}

// Spec §5.18: Pedal configuration (v1.4)
export interface PedalConfig {
  detect_mode: PedalDetectMode;
  sustain: PedalSustainConfig;
  expression: PedalExpressionConfig;
}

// Spec §5.18: Complete analog inputs configuration (v1.4)
export interface AnalogConfig {
  sliders: [SliderConfig, SliderConfig, SliderConfig]; // Exactly 3 sliders
  pedal: PedalConfig;
}

// Spec §5.17: Metadata
export interface ConfigMetadata {
  name: string;
  author?: string;
  created: string; // ISO 8601 timestamp
  modified?: string; // ISO 8601 timestamp
  description?: string;
  tags?: string[];
  firmware_tested?: string;
}

// Spec §5: Complete configuration (NORMATIVE)
export interface KeyboardConfig {
  config_version: ConfigVersion;
  keyboard: {
    modules: ModuleGrid;
  };
  note_mapping: NoteMapping;
  midi: MidiSettings;
  leds: LEDSettings & {
    default?: LEDDefaultBehavior;
  };
  layers: Layer[];
  zones?: Zone[]; // Optional (spec §5.6)
  overrides?: KeyOverride[]; // Optional
  led_overrides?: LEDOverride[]; // Optional
  led_layers?: LEDLayerConfig[]; // Optional
  led_scale?: ScaleColoringConfig; // Optional
  tuning?: {                       // v2.0: Microtonal tuning (optional)
    enabled: boolean;
    table: TuningTable;
  };
  analog?: AnalogConfig; // Optional (v1.4)
  metadata: ConfigMetadata;
}

// Helper type for validation results
export interface ValidationResult {
  valid: boolean;
  errors?: string[];
  config?: KeyboardConfig;
}
